// import axios from 'axios'
// import { atom, useAtom } from 'jotai'
// import { atomWithQuery } from 'jotai/query'
import React, { useEffect, useState, createContext, useContext } from 'react'
import {
    ITranslations,
    getLokaliseData,
    getLokaliseDataFromApi,
} from '../../store/lokalise/lokaliseAtom'
import { Spinner } from '../ui/Loaders/Spinner'
import myData from './translations.json'
// const lokaliseAtom = atom<ITranslations>({})

// const lAtom = atom((get) => {
//     return get(lokaliseAtom)
// })

interface IReplacement {
    [id: string]: string
}

const setLokalise = (translations: ITranslations): void => {
    localStorage.setItem('translations', JSON.stringify(translations))
}

const getLokalise = (): ITranslations | undefined => {
    const translations = localStorage.getItem('translations')

    return JSON.parse(translations)
}

// Add translation context
interface ITranslationContext {
    translations: ITranslations | undefined
    initialized: boolean
}

const TranslationContext = createContext<ITranslationContext>({
    translations: undefined,
    initialized: false,
})

// Updated L function that can work with context
const L = (key: string): string => {
    let value = '-'
    const lData = getLokalise()
    if (lData && lData[key]) {
        value = lData[key]
    }
    return value
}

// New hook for reactive translations
const useTranslations = (): ITranslationContext => {
    return useContext(TranslationContext)
}

// Reactive L function that uses context
const LReactive = (key: string): string => {
    const { translations } = useTranslations()
    let value = '-'
    if (translations && translations[key]) {
        value = translations[key]
    }
    return value
}

const L_HTML = (key: string, replacements?: IReplacement): JSX.Element => {
    let value = '-'
    const lData = getLokalise()
    if (lData && lData[key]) {
        value = lData[key]
    }
    let manipulated = value

    if (replacements && Object.keys(replacements).length) {
        Object.keys(replacements).forEach((identifier) => {
            // eslint-disable-next-line react/destructuring-assignment
            const s = replacements[identifier]
            manipulated = manipulated.replace(`%${identifier}%`, `${s}`)
        })
    }

    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: manipulated }} />
}

const Lokalise: React.FC = ({ children }) => {
    const [initialized, setInitialized] = useState(false)
    const [translations, setTranslations] = useState<ITranslations | undefined>(
        undefined
    )

    const initFallback = (): void => {
        if (myData) {
            setLokalise(myData)
            setTranslations(myData)
            setInitialized(true)
        }
    }

    const tryApiCall = async (attempt = 1): Promise<boolean> => {
        try {
            const response = await getLokaliseDataFromApi()

            if (response) {
                setLokalise(response)
                setTranslations(response)
                setInitialized(true)
                return true
            }
            return false
        } catch (error) {
            console.warn(`API call attempt ${attempt} failed:`, error)
            return false
        }
    }

    const init = async (): Promise<void> => {
        // First attempt - try API immediately
        const firstAttemptSuccess = await tryApiCall(1)
        if (firstAttemptSuccess) return

        // Wait 2-3 seconds, then retry API
        await new Promise((resolve) => setTimeout(resolve, 2500))
        const secondAttemptSuccess = await tryApiCall(2)
        if (secondAttemptSuccess) return

        // Set up fallback timeout for 10 seconds total
        const fallbackTimeout = setTimeout(() => {
            if (!initialized) {
                console.warn(
                    'API calls failed, falling back to local translations after 10 seconds'
                )
                initFallback()
            }
        }, 10000)

        // Try API a few more times with shorter intervals
        // eslint-disable-next-line no-plusplus
        for (let attempt = 3; attempt <= 5; attempt += 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 1500))
            // eslint-disable-next-line no-await-in-loop
            const success = await tryApiCall(attempt)
            if (success) {
                clearTimeout(fallbackTimeout)
                return
            }
        }

        // If we get here and still not initialized, the fallback timeout will handle it
    }

    useEffect(() => {
        init()
    }, [])

    if (!initialized) return <Spinner />

    return (
        <TranslationContext.Provider value={{ translations, initialized }}>
            {children}
        </TranslationContext.Provider>
    )
}

export { Lokalise, getLokaliseData, L, L_HTML, useTranslations, LReactive }
